import type { ApiError } from "@/types/api.types";
import { isApiError } from "@/types/api.types";

// Error messages for different error types
const ERROR_MESSAGES: Record<ApiError["type"], string> = {
  authentication: "Please log in to continue",
  authorization: "You don't have permission to do this",
  validation: "Please check your input and try again",
  not_found: "The requested resource was not found",
  rate_limit: "Too many requests. Please try again later",
  server_error: "Something went wrong. Please try again",
  unknown: "An unexpected error occurred",
};

// Get user-friendly error message
export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    return error.message || ERROR_MESSAGES[error.type];
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "An unexpected error occurred";
}

// Validation errors arrive from the backend as HTTP 400 with code "VALIDATION_ERROR",
// which categorizeErrorType maps to "unknown" — so detect by code OR type.
function isValidationError(error: ApiError): boolean {
  return error.type === "validation" || error.code === "VALIDATION_ERROR";
}

// Get field-level validation errors, normalized so each field maps to string[]
// (the backend sends one message per field as a plain string).
export function getValidationErrors(error: unknown): Record<string, string[]> | null {
  if (!isApiError(error) || !isValidationError(error) || !error.details) {
    return null;
  }
  const normalized: Record<string, string[]> = {};
  for (const [field, value] of Object.entries(error.details)) {
    normalized[field] = Array.isArray(value) ? value : [value];
  }
  return normalized;
}

// Check if error is of a specific type
export function isErrorType(error: unknown, type: ApiError["type"]): error is ApiError {
  return isApiError(error) && error.type === type;
}

// Create a standardized error for use in the application
export function createApiError(
  status: number,
  message: string,
  type?: ApiError["type"],
  details?: Record<string, string[]>
): ApiError {
  const errorType = type ?? categorizeErrorType(status);
  return {
    status,
    message,
    type: errorType,
    details,
  };
}

// Categorize error type based on HTTP status code
function categorizeErrorType(status: number): ApiError["type"] {
  switch (true) {
    case status === 401:
      return "authentication";
    case status === 403:
      return "authorization";
    case status === 404:
      return "not_found";
    case status === 422:
      return "validation";
    case status === 429:
      return "rate_limit";
    case status >= 500:
      return "server_error";
    default:
      return "unknown";
  }
}

// Log error for debugging (only in development)
export function logError(error: unknown, context?: string): void {
  if (import.meta.env.DEV) {
    console.error(`[Error${context ? ` - ${context}` : ""}]:`, error);
  }
}
